export const PACKAGE_SETTLE_VIEW_WX_OAUTH_MESSAGE_TYPE = "packageSettleViewWxOAuth";

export function buildPackageSettleViewWxCallbackUrl(): string {
	if (typeof window === "undefined") {
		return "/package-settle-view-wx-callback.html";
	}
	return `${window.location.origin}/package-settle-view-wx-callback.html`;
}

export interface PackageSettleViewWxOAuthMessage {
	type: typeof PACKAGE_SETTLE_VIEW_WX_OAUTH_MESSAGE_TYPE;
	code: string;
	state: string;
}

export function isPackageSettleViewWxOAuthMessage(
	data: unknown
): data is PackageSettleViewWxOAuthMessage {
	if (!data || typeof data !== "object") {
		return false;
	}
	const message = data as PackageSettleViewWxOAuthMessage;
	return (
		message.type === PACKAGE_SETTLE_VIEW_WX_OAUTH_MESSAGE_TYPE &&
		typeof message.code === "string" &&
		typeof message.state === "string"
	);
}

export interface PackageSettleViewAuthStatus {
	is_boss?: boolean;
	authorized?: boolean;
	expire_at?: number;
}

export interface PackageSettleViewWxReturnContext {
	returnHref: string;
	shopId: number;
}

const STORAGE_PREFIX = "package_settle_view_auth:";
export const PACKAGE_SETTLE_VIEW_WX_RETURN_KEY = "package_settle_view_wx_return";

function buildStorageKey(userId: number, shopId: number): string {
	return `${STORAGE_PREFIX}${userId}:${shopId}`;
}

export function readLocalPackageSettleViewAuth(
	userId: number,
	shopId: number
): PackageSettleViewAuthStatus | null {
	if (!userId || !shopId) {
		return null;
	}
	try {
		const raw = sessionStorage.getItem(buildStorageKey(userId, shopId));
		if (!raw) {
			return null;
		}
		const parsed = JSON.parse(raw) as PackageSettleViewAuthStatus;
		if (!parsed?.authorized) {
			return null;
		}
		if (parsed.expire_at && parsed.expire_at * 1000 <= Date.now()) {
			sessionStorage.removeItem(buildStorageKey(userId, shopId));
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
}

export function saveLocalPackageSettleViewAuth(
	userId: number,
	shopId: number,
	status: PackageSettleViewAuthStatus
): void {
	if (!userId || !shopId || !status?.authorized) {
		return;
	}
	sessionStorage.setItem(buildStorageKey(userId, shopId), JSON.stringify(status));
}

export function clearLocalPackageSettleViewAuth(userId: number, shopId: number): void {
	if (!userId || !shopId) {
		return;
	}
	sessionStorage.removeItem(buildStorageKey(userId, shopId));
}

export function buildPackageSettleViewWxState(shopId: number, returnPath?: string): string {
	const normalizedPath = (returnPath || "").replace(/^\/+/, "").split("?")[0];
	if (!normalizedPath) {
		return `packageSettleView-${shopId}`;
	}
	return `packageSettleView-${shopId}And${normalizedPath}`;
}

export function isPackageSettleViewWxState(state?: string | null): boolean {
	return Boolean(state && state.startsWith("packageSettleView-"));
}

export function parsePackageSettleViewWxMeta(state?: string | null): {
	shopId: number;
	returnPath: string;
} {
	if (!isPackageSettleViewWxState(state)) {
		return { shopId: 0, returnPath: "" };
	}
	const body = String(state).replace(/^packageSettleView-/, "");
	const splitIndex = body.indexOf("And");
	if (splitIndex === -1) {
		const shopId = Number(body);
		return {
			shopId: Number.isFinite(shopId) && shopId > 0 ? shopId : 0,
			returnPath: ""
		};
	}
	const shopId = Number(body.slice(0, splitIndex));
	const returnPath = body.slice(splitIndex + 3).replace(/^\/+/, "");
	return {
		shopId: Number.isFinite(shopId) && shopId > 0 ? shopId : 0,
		returnPath
	};
}

export function parsePackageSettleViewWxState(state?: string | null): number {
	return parsePackageSettleViewWxMeta(state).shopId;
}

/** 从 URL search 或 hash 查询串解析微信 OAuth 参数 */
export function parseWxOAuthQueryParams(href: string = window.location.href): URLSearchParams {
	const url = new URL(href);
	const params = new URLSearchParams(url.search);
	if (params.get("code") && params.get("state")) {
		return params;
	}
	if (url.hash.includes("?")) {
		const hashQuery = url.hash.split("?")[1] || "";
		const hashParams = new URLSearchParams(hashQuery.split("#")[0]);
		if (hashParams.get("code") && hashParams.get("state")) {
			return hashParams;
		}
	}
	return params;
}

export function savePackageSettleViewWxReturnContext(shopId: number, returnPath?: string): void {
	if (!shopId) {
		return;
	}
	const normalizedPath = (
		returnPath ||
		window.location.hash.replace(/^#\/?/, "") ||
		"takephoto"
	).split("?")[0];
	const context: PackageSettleViewWxReturnContext = {
		returnHref: `${window.location.origin}/#/${normalizedPath.replace(/^\/+/, "")}`,
		shopId
	};
	sessionStorage.setItem(PACKAGE_SETTLE_VIEW_WX_RETURN_KEY, JSON.stringify(context));
	try {
		localStorage.setItem(PACKAGE_SETTLE_VIEW_WX_RETURN_KEY, JSON.stringify(context));
	} catch {
		// ignore quota / privacy mode
	}
}

export function readPackageSettleViewWxReturnContext(): PackageSettleViewWxReturnContext | null {
	const readFrom = (storage: Storage) => {
		try {
			const raw = storage.getItem(PACKAGE_SETTLE_VIEW_WX_RETURN_KEY);
			if (!raw) {
				return null;
			}
			const parsed = JSON.parse(raw) as PackageSettleViewWxReturnContext;
			if (!parsed?.returnHref || !parsed.shopId) {
				return null;
			}
			return parsed;
		} catch {
			return null;
		}
	};

	return readFrom(sessionStorage) || readFrom(localStorage);
}

export function clearPackageSettleViewWxReturnContext(): void {
	sessionStorage.removeItem(PACKAGE_SETTLE_VIEW_WX_RETURN_KEY);
	try {
		localStorage.removeItem(PACKAGE_SETTLE_VIEW_WX_RETURN_KEY);
	} catch {
		// ignore
	}
}

function appendWxOAuthToHref(href: string, code: string, state: string): string {
	const url = new URL(href, window.location.origin);
	const hash = url.hash || "#/takephoto";
	const normalizedHash = hash.startsWith("#") ? hash.slice(1) : hash;
	const [hashPath, hashQuery = ""] = normalizedHash.split("?");
	const params = new URLSearchParams(hashQuery);
	params.set("code", code);
	params.set("state", state);
	const query = params.toString();
	url.hash = query ? `#${hashPath}?${query}` : `#${hashPath}`;
	url.search = "";
	return url.toString();
}

/** 微信 OAuth 回到站点根路径后，跳回扫码前页面（不走登录逻辑） */
export function redirectPackageSettleViewWxReturn(code: string, state: string): void {
	const { returnPath } = parsePackageSettleViewWxMeta(state);
	const context = readPackageSettleViewWxReturnContext();

	let hashPath = returnPath;
	if (!hashPath && context?.returnHref) {
		try {
			hashPath = new URL(context.returnHref).hash.replace(/^#\/?/, "").split("?")[0];
		} catch {
			hashPath = "";
		}
	}
	if (!hashPath) {
		hashPath = "takephoto";
	}

	const targetHref = `${window.location.origin}/#/${hashPath.replace(/^\/+/, "")}`;
	window.location.href = appendWxOAuthToHref(targetHref, code, state);
}

/** 检测并处理根路径上的套餐结算价微信回调（需在路由默认跳转前调用） */
export function tryRedirectPackageSettleViewWxReturnFromSearch(): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	const params = new URLSearchParams(window.location.search);
	const code = params.get("code");
	const state = params.get("state");
	if (!code || !isPackageSettleViewWxState(state)) {
		return false;
	}
	redirectPackageSettleViewWxReturn(code, state);
	return true;
}

export function getPackageSettleViewWxRedirectParams(): { code: string; shopId: number } | null {
	const params = parseWxOAuthQueryParams();
	const code = params.get("code");
	const state = params.get("state");
	if (!isPackageSettleViewWxState(state) || !code) {
		return null;
	}
	const shopId = parsePackageSettleViewWxState(state);
	if (!shopId) {
		return null;
	}
	return { code, shopId };
}

export function cleanupPackageSettleViewWxRedirectUrl(): void {
	const params = parseWxOAuthQueryParams();
	if (!params.get("code") || !isPackageSettleViewWxState(params.get("state"))) {
		return;
	}

	const url = new URL(window.location.href);
	if (url.hash.includes("?")) {
		const [hashPath, hashQuery = ""] = url.hash.slice(1).split("?");
		const hashParams = new URLSearchParams(hashQuery);
		hashParams.delete("code");
		hashParams.delete("state");
		const query = hashParams.toString();
		url.hash = query ? `#${hashPath}?${query}` : `#${hashPath}`;
	}
	url.searchParams.delete("code");
	url.searchParams.delete("state");
	const search = url.searchParams.toString();
	window.history.replaceState(
		null,
		"",
		`${url.pathname}${search ? `?${search}` : ""}${url.hash}`
	);
}
